import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { resourcesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import TableComponent from '../../components/TableComponent';
import Drawer from '../../components/Drawer';
import ConfirmDialog from '../../components/ConfirmDialog';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { localDateISO } from '../../lib/utils';

function ResourceForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    usePageTitle(isEditing ? 'Editar Recurso' : 'Novo Recurso');
    const { toast, toastApiError } = useToast();

    const [resource, setResource] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [costHistory, setCostHistory] = useState([]);
    const [costHistoryLoading, setCostHistoryLoading] = useState(false);
    const [costDrawerOpen, setCostDrawerOpen] = useState(false);
    const [costLoading, setCostLoading] = useState(false);
    const [costFormData, setCostFormData] = useState({
        new_cost: '',
        notes: '',
        entry_date: localDateISO(),
    });
    const [costEditingId, setCostEditingId] = useState(null);
    const [costDeleteOpen, setCostDeleteOpen] = useState(false);
    const [costRowPendingDelete, setCostRowPendingDelete] = useState(null);

    const [formData, setFormData] = useState({
        code: '',
        description: '',
        unit: '',
        unit_cost: '',
        operators_count: '',
        availability: {
            mon: '',
            tue: '',
            wed: '',
            thu: '',
            fri: '',
            sat: '',
            sun: '',
        },
        status: 'active',
    });

    useEffect(() => {
        if (isEditing) {
            loadResource();
            loadCostHistory();
        } else {
            setLoading(false);
        }
    }, [id, isEditing]);

    const loadResource = async () => {
        setLoading(true);
        try {
            const response = await resourcesService.get(id);
            const data = response.data.data || response.data;
            setResource(data);
            setFormData({
                code: data.code || '',
                description: data.description || '',
                unit: data.unit || '',
                unit_cost: data.unit_cost || '',
                operators_count: data.operators_count || '',
                availability: {
                    mon: data.availability?.mon || '',
                    tue: data.availability?.tue || '',
                    wed: data.availability?.wed || '',
                    thu: data.availability?.thu || '',
                    fri: data.availability?.fri || '',
                    sat: data.availability?.sat || '',
                    sun: data.availability?.sun || '',
                },
                status: data.status || 'active',
            });
        } catch (error) {
            console.error('Error loading resource:', error);
            toast({
                title: 'Erro ao carregar',
                description: 'Não foi possível carregar o recurso',
                variant: 'error',
            });
            navigate('/production/resources');
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleAvailabilityChange = (dayKey, e) => {
        setFormData(prev => ({
            ...prev,
            availability: {
                ...(prev.availability || {}),
                [dayKey]: e.target.value,
            },
        }));
    };

    const loadCostHistory = async () => {
        if (!id) return;

        setCostHistoryLoading(true);
        try {
            const response = await resourcesService.getCostHistory(id);
            const raw = response.data;
            const data = raw?.data ?? raw;
            setCostHistory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading cost history:', error);
            setCostHistory([]);
        } finally {
            setCostHistoryLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    const formatLaunchDate = (row) => {
        const raw = row.entry_date ?? row.created_at;
        if (!raw) return '-';
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
            const [y, m, d] = raw.slice(0, 10).split('-');
            return `${d}/${m}/${y}`;
        }
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const launchDateFromRow = (row) => {
        const raw = row.entry_date ?? row.created_at;
        if (!raw) return localDateISO();
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
        try {
            return localDateISO(new Date(raw));
        } catch {
            return localDateISO();
        }
    };

    const handleCostFormChange = (e) => {
        setCostFormData({
            ...costFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleCostSubmit = async (e) => {
        e.preventDefault();
        setCostLoading(true);
        try {
            const newCost = parseFloat(String(costFormData.new_cost).replace(',', '.')) || 0;
            const payload = {
                entry_date: costFormData.entry_date,
                new_cost: newCost,
                notes: costFormData.notes?.trim() ? costFormData.notes : null,
            };

            if (costEditingId) {
                await resourcesService.patchCostHistory(id, costEditingId, payload);
            } else {
                await resourcesService.createCostHistory(id, payload);
            }

            await loadResource();
            await loadCostHistory();

            setCostDrawerOpen(false);
            setCostEditingId(null);
            setCostFormData({
                new_cost: '',
                notes: '',
                entry_date: localDateISO(),
            });
            toast({
                title: 'Custo atualizado',
                description: 'Alteração registrada no histórico',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error updating cost:', error);
            toastApiError(error, { title: 'Erro ao atualizar custo', fallback: 'Não foi possível atualizar o custo' });
        } finally {
            setCostLoading(false);
        }
    };

    const handleConfirmDeleteCost = async () => {
        if (!costRowPendingDelete?.id) return;
        setCostLoading(true);
        try {
            await resourcesService.deleteCostHistory(id, costRowPendingDelete.id);
            await loadResource();
            await loadCostHistory();
            toast({
                title: 'Lançamento excluído',
                description: 'O histórico de custo foi atualizado.',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error deleting cost history:', error);
            toastApiError(error, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o lançamento.' });
        } finally {
            setCostLoading(false);
            setCostDeleteOpen(false);
            setCostRowPendingDelete(null);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);

        try {
            const payload = {
                ...formData,
                operators_count: formData.operators_count ? parseInt(formData.operators_count) : 0,
            };

            if (isEditing) {
                await resourcesService.update(id, payload);
            } else {
                // Backend inicia com unit_cost = 0; custo é alterado via /cost-history
                const response = await resourcesService.create({ ...payload, unit_cost: 0 });
                const saved = response.data?.data || response.data;
                if (saved) setResource(saved);
            }
            toast({
                title: 'Recurso salvo',
                description: 'Alterações salvas com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error saving resource:', error);
            toastApiError(error, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o recurso' });
        } finally {
            setFormLoading(false);
        }
    };

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title={isEditing ? `Editar Recurso - ${resource?.code ?? ''}` : 'Novo Recurso'}
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/resources' },
                    ]}
                    actions={[
                        {
                            label: 'Voltar',
                            icon: 'back',
                            variant: 'outline',
                            as: Link,
                            to: '/production/resources',
                        },
                        {
                            label: formLoading ? 'Salvando...' : 'Salvar',
                            icon: 'save',
                            onClick: () => {
                                const form = document.getElementById('resource-form');
                                if (form) {
                                    form.requestSubmit();
                                }
                            },
                            disabled: formLoading || loading,
                        },
                    ]}
                />

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="general" activeValue={activeTab} onValueChange={setActiveTab}>
                            Geral
                        </TabsTrigger>
                        {isEditing && (
                            <TabsTrigger value="cost-history" activeValue={activeTab} onValueChange={setActiveTab}>
                                Custo
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Tab: Geral */}
                    <TabsContent value="general" activeValue={activeTab} className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Informações do Recurso</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form id="resource-form" onSubmit={handleFormSubmit} className="space-y-6" autoComplete="off">
                                    {/* Seção: Identificação */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Identificação
                                            </h3>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Código *
                                                    </label>
                                                    <Input
                                                        name="code"
                                                        value={formData.code}
                                                        onChange={handleFormChange}
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Unidade *
                                                    </label>
                                                    <Input
                                                        name="unit"
                                                        value={formData.unit}
                                                        onChange={handleFormChange}
                                                        placeholder="h, min, etc"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Descrição *
                                                </label>
                                                <Input
                                                    name="description"
                                                    value={formData.description}
                                                    onChange={handleFormChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seção: Operadores */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Operadores
                                            </h3>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Quantidade de Operadores
                                            </label>
                                            <Input
                                                type="number"
                                                name="operators_count"
                                                value={formData.operators_count}
                                                onChange={handleFormChange}
                                                min="0"
                                            />
                                        </div>
                                    </div>

                                    {/* Seção: Disponibilidade Semanal */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Disponibilidade Semanal
                                            </h3>
                                        </div>

                                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                                            {[
                                                { key: 'mon', label: 'Segunda' },
                                                { key: 'tue', label: 'Terça' },
                                                { key: 'wed', label: 'Quarta' },
                                                { key: 'thu', label: 'Quinta' },
                                                { key: 'fri', label: 'Sexta' },
                                                { key: 'sat', label: 'Sábado' },
                                                { key: 'sun', label: 'Domingo' },
                                            ].map((day) => (
                                                <div key={day.key}>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        {day.label}
                                                    </label>
                                                    <MaskedInput
                                                        mask="time"
                                                        name={`availability_${day.key}`}
                                                        value={formData.availability?.[day.key] || ''}
                                                        onChange={(e) => handleAvailabilityChange(day.key, e)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Seção: Status */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-md font-semibold text-gray-900">
                                                Status
                                            </h3>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Status *
                                            </label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleFormChange}
                                                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                            >
                                                <option value="active">Ativo</option>
                                                <option value="inactive">Inativo</option>
                                            </select>
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: Custo - apenas se estiver editando */}
                    {isEditing && (
                        <TabsContent value="cost-history" activeValue={activeTab} className="mt-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Histórico de Alteração de Custos</CardTitle>
                                        <Button onClick={() => {
                                            setCostEditingId(null);
                                            setCostFormData({
                                                new_cost: formData.unit_cost || '',
                                                notes: '',
                                                entry_date: localDateISO(),
                                            });
                                            setCostDrawerOpen(true);
                                        }}>
                                            Alterar Custo
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {costHistoryLoading && costHistory.length === 0 ? null : (
                                        <TableComponent
                                            columns={[
                                                {
                                                    key: 'entry_date',
                                                    label: 'Data do lançamento',
                                                    render: (_value, row) => formatLaunchDate(row),
                                                },
                                                {
                                                    key: 'old_cost',
                                                    label: 'Custo Anterior',
                                                    render: (value) => formatCurrency(value),
                                                },
                                                {
                                                    key: 'new_cost',
                                                    label: 'Custo Novo',
                                                    icon: 'new',
                                                    render: (value) => (
                                                        <span className="font-semibold text-green-600">
                                                            {formatCurrency(value)}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    key: 'difference',
                                                    label: 'Variação',
                                                    render: (value, row) => {
                                                        const diff = parseFloat(value || row.new_cost - row.old_cost || 0);
                                                        const diffPercent = row.old_cost > 0
                                                            ? ((diff / row.old_cost) * 100).toFixed(2)
                                                            : '0.00';
                                                        return (
                                                            <div className="flex flex-col">
                                                                <span className={diff >= 0 ? 'text-red-600' : 'text-green-600'}>
                                                                    {diff >= 0 ? '+' : ''}{formatCurrency(diff)}
                                                                </span>
                                                                <span className={`text-xs ${diff >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                                                                    ({diff >= 0 ? '+' : ''}{diffPercent}%)
                                                                </span>
                                                            </div>
                                                        );
                                                    },
                                                },
                                                {
                                                    key: 'user',
                                                    label: 'Usuário',
                                                    render: (value, row) => row.user?.name || '-',
                                                },
                                                {
                                                    key: 'notes',
                                                    label: 'Observações',
                                                    render: (value) => value || '-',
                                                },
                                            ]}
                                            data={costHistory}
                                            emptyMessage="Nenhuma alteração de custo registrada"
                                            pagination={false}
                                            onEdit={(row) => {
                                                setCostEditingId(row.id);
                                                setCostFormData({
                                                    new_cost: row.new_cost != null ? String(row.new_cost) : '',
                                                    notes: row.notes || '',
                                                    entry_date: launchDateFromRow(row),
                                                });
                                                setCostDrawerOpen(true);
                                            }}
                                            onDelete={(row) => {
                                                setCostRowPendingDelete(row);
                                                setCostDeleteOpen(true);
                                            }}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>

                {/* Drawer de Alteração de Custo */}
                <Drawer
                    open={costDrawerOpen}
                    onClose={() => {
                        setCostDrawerOpen(false);
                        setCostEditingId(null);
                        setCostFormData({
                            new_cost: '',
                            notes: '',
                            entry_date: localDateISO(),
                        });
                    }}
                    title={costEditingId ? 'Editar lançamento de custo' : 'Alterar Custo do Recurso'}
                    size="md"
                    footer={
                        <div className="flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setCostDrawerOpen(false);
                                    setCostEditingId(null);
                                    setCostFormData({
                                        new_cost: '',
                                        notes: '',
                                        entry_date: localDateISO(),
                                    });
                                }}
                                disabled={costLoading}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" form="resource-cost-form" disabled={costLoading}>
                                {costLoading ? 'Salvando…' : 'Salvar'}
                            </Button>
                        </div>
                    }
                >
                    <form id="resource-cost-form" onSubmit={handleCostSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Data do lançamento *
                            </label>
                            <MaskedInput
                                mask="date"
                                name="entry_date"
                                value={costFormData.entry_date}
                                onChange={handleCostFormChange}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Custo Atual
                            </label>
                            <Input
                                type="text"
                                value={formatCurrency(formData.unit_cost || 0)}
                                disabled
                                className="bg-gray-50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Novo Custo *
                            </label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                name="new_cost"
                                value={costFormData.new_cost}
                                onChange={handleCostFormChange}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Observações
                            </label>
                            <textarea
                                name="notes"
                                value={costFormData.notes}
                                onChange={handleCostFormChange}
                                rows={4}
                                className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>

                    </form>
                </Drawer>

                <ConfirmDialog
                    open={costDeleteOpen}
                    title="Excluir lançamento?"
                    message="Esta ação remove o registro do histórico e recalcula o custo unitário atual conforme os demais lançamentos."
                    onConfirm={handleConfirmDeleteCost}
                    onCancel={() => {
                        setCostDeleteOpen(false);
                        setCostRowPendingDelete(null);
                    }}
                />
            </div>
        </AppLayout>
    );
}

export default ResourceForm;




